import RequestSaveForm from "@app-components/modal/RequestSaveModal";
import { NodeData, useLocalFiles } from "@app-utils/localStorage/localFiles";
import { useRefCallback } from "@enfusion-ui/hooks";
import {
  Button,
  IconButton,
  Modal,
  Portal,
  useConfirmationModal,
} from "@enfusion-ui/web-components";
import {
  errorToast,
  styled,
  successToast,
  useTabs,
  useThisTab,
} from "@enfusion-ui/web-core";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faEllipsisV, faSave } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Menu, { MenuItem } from "rc-menu";
import * as React from "react";
import { v4 as uuidv4 } from "uuid";

import {
  InspectorContextValues,
  RequestTypes,
  useInspectorContext,
} from "../../context/inspector/context";
import { HTTPConfig } from "./HTTPConfig";
import { OEMSConfig } from "./OEMSConfig";
import { ReportConfig } from "./ReportConfig";
import { RestConfig } from "./RestConfig";

export const ActionButton = styled(Button)`
  height: 38px;
  width: 110px !important;
  margin-right: var(--spacing);
`;

export const ButtonArea = styled.div`
  margin-top: auto;
  display: flex;
`;

export const PathArea = styled.div`
  flex: 1;
  margin-right: var(--spacing);
`;

const ActionContainer = styled.div<{ background?: boolean }>`
  margin: auto;
  right: 0px;
  z-index: var(--widget-z);
  border: 1px solid transparent;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background: ${({ background: b }) => (b ? "var(--primary)" : "transparent")};
  border-radius: 5px;
`;

const MenuItemsTitle = styled.span`
  font-size: 14px;
  line-height: 14px;
  color: var(--text-normal);
  margin: 0px var(--spacing-l);
`;

const MenuItemsIcon = styled(FontAwesomeIcon)`
  width: 11px !important;
`;

export const InspectorConfig = () => {
  const {
    requestType,
    apiSource,
    config,
    environment,
    requiresTwoFactorAuthentication,
  } = useInspectorContext();
  const { id, renameTab } = useThisTab();
  const { savedRequests, setSavedRequests } = useLocalFiles();
  const { tabs } = useTabs();
  const [saveModalOpen, setSaveModalOpen] = React.useState<boolean>(false);
  const [optionsPortalOpen, setOptionsPortalOpen] =
    React.useState<boolean>(false);
  const requestNameRef = React.useRef("");
  const storedRequestNames = React.useRef(Object.keys(savedRequests));
  const currentTabDef = tabs.find((tab) => tab.id === id);
  React.useEffect(() => {
    storedRequestNames.current = Object.keys(savedRequests);
  }, [savedRequests]);
  const editRequestRef = React.useRef(false);
  const menuIconRef = React.useRef<HTMLDivElement | null>(null);

  const nodeConfig = React.useMemo(
    () =>
      ({
        requestType,
        apiSource,
        config,
        environment,
        requiresTwoFactorAuthentication,
      } as InspectorContextValues),
    [
      requestType,
      apiSource,
      config,
      environment,
      requiresTwoFactorAuthentication,
    ]
  );

  const saveRequestInStorage = useRefCallback(
    async (name: string, directory?: NodeData | null) => {
      const id = uuidv4();
      const newFile: NodeData = {
        id,
        name,
        path: `/${name}`,
        file: true,
        level: 0,
        config: nodeConfig,
      };
      if (directory) {
        newFile.depth = 1;
        newFile.path = `${directory.path}/${name}`;

        const previousNodes: NodeData[] = directory.nodes || [];
        const existingRequest = previousNodes.filter(
          (element) => element.name.trim() === newFile.name.trim()
        );

        if (existingRequest.length === 0) {
          setSavedRequests((p) => ({
            ...p,
            [directory.name]: {
              ...directory,
              nodes: [...previousNodes, newFile],
            },
          }));
        } else {
          errorToast("File with same name already exists.");
        }
      } else {
        setSavedRequests((p) => ({ ...p, [name]: newFile }));
      }
    },
    [nodeConfig]
  );

  const handleUpdateRequest = useRefCallback((folderNode?: NodeData) => {
    try {
      if (folderNode) {
        setSavedRequests((p) => ({
          ...p,
          [folderNode.name]: { ...folderNode },
        }));
        successToast("Updated the saved request.");
        return;
      }
      saveRequestInStorage(id);
      successToast("Updated the saved request.");
    } catch (error) {
      errorToast("Unable to update the request. " + error);
    }
  }, []);

  const handleEditRequest = useRefCallback(
    (reportName: string, directory?: NodeData) => {
      saveRequestInStorage(reportName, directory);
      renameTab(reportName);
    },
    []
  );

  const handleRequestSave = useRefCallback(
    (reportName: string, directory?: NodeData) => {
      if (editRequestRef.current) {
        handleEditRequest(reportName, directory);
        setSaveModalOpen(false);
        return;
      }
      const existing = savedRequests[reportName];
      if (existing) {
        requestNameRef.current = reportName;
        overwriteConfirmation.openModal();
        setSaveModalOpen(false);
        return;
      }
      try {
        saveRequestInStorage(reportName, directory);
        renameTab(reportName);
        successToast("Successfully saved the request.");
      } catch (error) {
        errorToast("Unable to save the request. " + error);
      } finally {
        setSaveModalOpen(false);
      }
    },
    [savedRequests]
  );

  const handleOverwriteRequest = useRefCallback(async () => {
    try {
      saveRequestInStorage(requestNameRef.current);
      renameTab(requestNameRef.current);
      successToast("Successfully saved the request.");
    } catch (error) {
      errorToast("Unable to overwrite the request. " + error);
    } finally {
      overwriteConfirmation.closeModal();
    }
  }, [savedRequests]);

  const handleSave = useRefCallback(() => {
    if (currentTabDef?.component === "inspector") {
      setSaveModalOpen(true);
      return;
    }
    if (storedRequestNames.current.includes(id)) {
      handleUpdateRequest();
    } else {
      Object.values(savedRequests).forEach((node: NodeData) => {
        if (!node.file && node.nodes?.length) {
          node.nodes.forEach((childNode: NodeData) => {
            if (childNode.name === id) {
              childNode.config = nodeConfig;
              handleUpdateRequest(node);
            }
          });
        }
      });
    }
  }, [nodeConfig, savedRequests, currentTabDef]);

  const handleSaveAs = () => {
    editRequestRef.current = true;
    setSaveModalOpen(true);
  };

  const closeSaveModal = () => {
    setSaveModalOpen(false);
  };

  const SaveModal = useRefCallback(
    () => (
      <Modal
        isOpen={saveModalOpen}
        onClose={closeSaveModal}
        title="Save Request"
        content={
          <RequestSaveForm
            onSubmit={handleRequestSave}
            onCancel={closeSaveModal}
            requestType={requestType}
            nodes={savedRequests}
          />
        }
      />
    ),
    [saveModalOpen, handleRequestSave, closeSaveModal]
  );

  const overwriteConfirmation = useConfirmationModal({
    title: "Overwrite Request",
    renderContent: () => `A Request with name ${requestNameRef.current} already
    exists. Do you want to overwrite?`,
    onSubmit: handleOverwriteRequest,
    submitActionTheme: "primary",
  });

  const SaveRequest = () => {
    return (
      <ButtonArea>
        <ActionButton icon={faSave} onClick={handleSave}>
          Save
        </ActionButton>

        <ActionContainer ref={menuIconRef}>
          <IconButton
            hoverable
            icon={faEllipsisV}
            title="Options"
            onClick={() => setOptionsPortalOpen(true)}
            size="sm"
            data-e2e-id="report-view-action-options"
          />
        </ActionContainer>

        <Portal
          open={optionsPortalOpen}
          attachedRef={menuIconRef}
          align="right"
          onClickOutside={() => setOptionsPortalOpen(false)}
        >
          <Menu>
            <MenuItem
              onClick={handleSaveAs}
              disabled={currentTabDef?.component === "inspector"}
              data-e2e-id="report-view-action-options-save-as"
            >
              <MenuItemsIcon icon={faSave as IconProp}></MenuItemsIcon>
              <MenuItemsTitle>Save As</MenuItemsTitle>
            </MenuItem>
          </Menu>
        </Portal>
      </ButtonArea>
    );
  };

  const configType = React.useMemo(() => {
    switch (requestType) {
      case RequestTypes.HTTP:
        return <HTTPConfig />;
      case RequestTypes.REPORT:
        return <ReportConfig />;
      case RequestTypes.ENFUSION:
        return <RestConfig />;
      case RequestTypes.OEMS:
        return <OEMSConfig />;
      default:
        return null;
    }
  }, [requestType]);

  return (
    <>
      {configType}
      {overwriteConfirmation.content}
      <SaveModal />
      <SaveRequest />
    </>
  );
};
